import React from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { generateAndExecuteMutation } from "../../../../data/Axios/DynamicService";
import { AccountsandFinanceurl } from "../../../../config";
import Header from "../../../../components/Header"; // Assuming Header path; adjust if needed

const base_url = AccountsandFinanceurl.uri;

const BankForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const initialValues = {
    id: props.data ? props.data.id : "",
    bankName: props.data ? props.data.bankName : "",
    bankSwiftCode: props.data ? props.data.bankSwiftCode : "",
    finCode: props.data ? props.data.finCode : "",
    mobile: props.data ? props.data.mobile : "",
    email: props.data ? props.data.email : "",
    createdDate: props.data ? props.data.createdDate : "",
    createdBy: props.data ? props.data.createdBy : userId,
    companyId: props.data ? props.data.companyId : companyId,
    company: props.data ? props.data.company : null,
  };

  const validationSchema = yup.object().shape({
    bankName: yup.string().required("Bank Name is required").nullable(),
    mobile: yup
      .string()
      .matches(
        /^(0[17]\d{8}|\+254[17]\d{8}|254[17]\d{8})$/,
        "Enter a valid phone number"
      )
      .required("Phone number is required")
      .nullable(),
    email: yup
      .string()
      .email("Enter a valid email address")
      .required("Email is required")
      .nullable(),
    bankSwiftCode: yup.string().nullable(),
    finCode: yup.string().nullable(),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        id,
        bankName,
        bankSwiftCode,
        finCode,
        mobile,
        email,
        createdDate,
        createdBy,
        companyId,
        company,
      } = values;

      const mutationData = props.isEditing
        ? {
            id,
            bankName,
            bankSwiftCode,
            finCode,
            mobile,
            email,
            createdDate,
            createdBy,
            companyId: companyId || null,
            company,
          }
        : {
            bankName,
            bankSwiftCode,
            finCode,
            mobile,
            email,
            createdDate: new Date().toISOString(),
            createdBy: userId,
            companyId: companyId || null,
            company,
          };

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateBank" : "createBank",
        mutationData,
        props.isEditing ? "updatedBank" : "newBank",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Bank ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Bank" : "Create Bank"}
        subtitle={
          props.isEditing ? "Edit an Existing Bank" : "Create a New Bank"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4",
                },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Bank Name *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.bankName}
                name="bankName"
                error={touched.bankName && !!errors.bankName}
                helperText={touched.bankName && errors.bankName}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Bank Swift Code"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.bankSwiftCode}
                name="bankSwiftCode"
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="FIN Code"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.finCode}
                name="finCode"
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Phone Number *"
                placeholder="+254712345678 or 0712345678"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.mobile}
                name="mobile"
                error={touched.mobile && !!errors.mobile}
                helperText={touched.mobile && errors.mobile}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Email *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                name="email"
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                sx={{
                  backgroundColor: '#00A676',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#00A676',
                    boxShadow: '0px 4px 12px rgba(0, 166, 118, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#B0B0B0',
                    color: 'white',
                  },
                }}
              >
                {isSubmitting
                  ? props.isEditing
                    ? "Updating..."
                    : "Saving..."
                  : props.isEditing
                  ? "Update Bank"
                  : "Save Bank"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default BankForm;